<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="mt-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-multiple
              :is-busy="isProcessing"
              v-model="filters.owner"
              style="min-width: 260px;"
              :options="ownerOptions"
              placeholder="Select Owner Name"
              :activate-search="true"
            />
          </div>
          <div class="flex items-center w-full">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-6"
        @fetch="fetchSpotCosts()"
        ref="tableTop"
      >
        <template #start>
          <div
            class="flex items-center justify-center"
          >
            <vs-button
              class="button w-max font-semibold"
              color="primary"
              @click="startAdd"
              v-show="isUserCanAccessMenu(permission.name, 'add')"
            >
              Add New Freight Cost
            </vs-button>
            <vs-button
              class="button w-max font-semibold ml-5"
              color="secondary"
              @click="isDownload = true"
            >
              Export
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="mt-6"
        :table-header="spotCostHeaders"
        :table-data="spotCostData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        :frozen-columns="isActionColumnVisible ? 5 : 4"
        :loading="isBusy"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row">
            <template v-if="row.isEditing">
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`mx-1 self-center flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click="isProcessing? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'edit')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-edit.svg')"
                  alt="edit"
                >
              </div>
              <div
                v-show="isUserCanAccessMenu(permission.name, 'delete')"
                class="mx-1 self-center flex items-center justify-center icon-transparent-bg"
                @click="popUp = { isShown: true, tempId: row.id }"
              >
                <img
                  :src="require('@/assets/shipo-icon-delete.svg')"
                  alt="delete"
                >
              </div>
            </template>
          </div>
        </template>
        <template #owner_id="{ row }">
          <template v-if="row.isEditing">
            <div
              class="relative"
              style="width: 18rem;"
            >
              <div>
                <select-with-search
                  :is-busy="isProcessing"
                  :disabled="isProcessing"
                  v-model="row.editForm.owner_id.value"
                  :class="{ 'is-not-valid': !row.editForm.owner_id.isValid }"
                  :options="ownerOptions"
                  placeholder="Owner"
                />
              </div>
              <span
                v-if="!row.editForm.owner_id.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.owner_name || '-' }}
          </template>
        </template>
        <template #barge_size="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  v-model="row.editForm.barge_size.value"
                  :class="{ 'is-not-valid': !row.editForm.barge_size.isValid }"
                  class="vs-input--input w-40"
                  :options="globalNumericOptions"
                  :disabled="isProcessing"
                />
              </div>
              <span
                v-if="!row.editForm.barge_size.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.barge_size ? handleValidateAndFormatNumber(row.barge_size) : 0 }}
          </template>
        </template>
        <template #route="{ row }">
          <template v-if="row.isEditing">
            <div>
              <select-with-multiple
                :disabled="isProcessing"
                v-model="row.editForm.route.value"
                class="px-2"
                :options="routeOptions"
                :grouped="true"
                :activate-search="true"
                :is-can-add="true"
                :on-add="handleAddRoute"
                :is-add-one-by-one="true"
                separator="-"
                placeholder="Route"
                :allow-duplicate-values="true"
                :danger="!row.editForm.route.value.length"
                danger-text="* Required"
              />
            </div>
          </template>
          <template v-else>
            {{ row.route || '-' }}
          </template>
        </template>
        <template #contract_number="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  :disabled="isProcessing"
                  v-model="row.editForm.contract_number.value"
                  :class="{ 'is-not-valid': !row.editForm.contract_number.isValid }"
                  placeholder="Contract No."
                  style="width: 150px"
                />
              </div>
              <span
                v-if="!row.editForm.contract_number.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.contract_number || '-' }}
          </template>
        </template>
        <template #rent_price_idr="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <v-autonumeric
                  :disabled="isProcessing"
                  v-model="row.editForm.rent_price_idr.value"
                  :class="{ 'is-not-valid': !row.editForm.rent_price_idr.isValid }"
                  class="vs-input--input w-40"
                  style="width: 150px"
                  :options="globalNumericOptions"
                />
              </div>
              <span
                v-if="!row.editForm.rent_price_idr.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.rent_price_idr ? handleValidateAndFormatNumber(row.rent_price_idr) : 0 }}
          </template>
        </template>
        <template #contract_end="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <date-picker
                  v-model="row.editForm.contract_end.value"
                  type="date"
                  value-type="YYYY-MM-DD"
                  format="DD-MMM-YYYY"
                  :class="{ 'is-not-valid': !row.editForm.contract_end.isValid }"
                  placeholder="Expiry Date"
                  :disabled="isProcessing"
                />
              </div>
              <span
                v-if="!row.editForm.contract_end.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ moment(row.contract_end).utc().format('DD MMM YYYY') || '-' }}
          </template>
        </template>
        <template #remark="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-textarea
                  :disabled="isProcessing"
                  v-model="row.editForm.remark.value"
                  class="py-1"
                  placeholder="Description"
                  width="200px"
                />
              </div>
            </div>
          </template>
          <template v-else>
            {{ row.remark || '-' }}
          </template>
        </template>
        <template #attachments="{ row }">
          <template v-if="row.isEditing">
            <div class="flex flex-row items-center">
              <input
                type="file"
                id="fileInput"
                ref="fileInput"
                class="hidden"
                accept="image/*,application/pdf"
                :disabled="isProcessing"
                @change="handleFileUpload($event, row)"
              >
              <vs-button
                class="bg-secondary-accent text-secondary py-1 w-64 h-8"
                color="primary"
                type="button"
                :disabled="isProcessing"
                @click="$refs.fileInput.click()"
              >
                <div class="flex flex-row items-center justify-start pr-2 w-full">
                  <img
                    :src="require('@/assets/shipo-icon-edit-upload.svg')"
                    class="flex-shrink-0 mr-2 h-6"
                  >
                  <div class="truncate">
                    {{
                      row.editForm.attachments.value
                        ? row.editForm.attachments.file
                          ? row.editForm.attachments.file.name
                          : row.editForm.attachments.value.name
                        : 'Select File'
                    }}
                  </div>
                </div>
              </vs-button>
            </div>
            <span
              v-if="!row.editForm.attachments.value"
              class="text-danger text-sm absolute-bottom-5 left-0"
            >*Required</span>
          </template>
          <template v-else>
            <vs-button
              class="bg-secondary text-white py-1 w-64 h-8"
              color="primary"
              type="button"
              @click="openDocumentModal(row)"
              :disabled="!(row.attachments && row.attachments.length > 0) || isProcessing"
            >
              <div class="flex flex-row items-center justify-start pr-2 w-full">
                <img
                  :src="require('@/assets/shipo-icon-see.svg')"
                  class="flex-shrink-0 mr-2 h-6"
                >
                <div class="truncate">
                  {{ row.attachments.length ? row.attachments[0].name : 'No document' }}
                </div>
              </div>
            </vs-button>
          </template>
        </template>
        <template #status_approval="{ row }">
          <div
            :class="`badge-status ${row.status_approval && row.status_approval.toLowerCase() === 'acknowledged' ? '' : 'inactive'} px-3`"
          >
            {{ row.status_approval || 'PENDING ACKNOWLEDGE' }}
          </div>
        </template>
      </data-table>
      <table-bottom
        class="mt-6"
        @fetch="fetchSpotCosts"
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      is-warning
      title="Delete Freight Cost"
      left-button="Cancel"
      right-button="Delete"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null }"
      @leftButton="popUp = { isShown: false, tempId: null }"
      @rightButton="deleteSpotCost"
    >
      <p class="text-center mt-2">
        Are you sure you want to delete
      </p>
      <p class="text-center mt-2">
        "{{ popUp.tempId ? spotCostData.find((item) => item.id === popUp.tempId).contract_number : '' }}"?
      </p>
      <p class="text-center mt-2">
        This action cannot be undone
      </p>
    </pop-up>
    <pop-up
      :active.sync="popUpSuccess"
      @close="resetTableFilter"
      @ok="resetTableFilter"
    >
      You have successfully delete Freight Cost
    </pop-up>
    <pop-up
      :active.sync="popUp.isRestoreShown"
      is-warning
      title="Restore Freight Cost"
      left-button="Cancel"
      right-button="Restore"
      :is-busy="isProcessing"
      @close="closeRestoreModal"
      @leftButton="closeRestoreModal"
      @rightButton="restoreSpotCostData"
    >
      <p class="text-center mt-1">
        Freight cost {{ restoreData.contract_number }} already exists but deleted.
      </p>
      <p class="text-center">
        Would you like to restore?
      </p>
    </pop-up>
    <base-custom-modal
      :is-shown="isDocumentModalShown"
      @update:isShown="isDocumentModalShown=$event"
    >
      <template #title>
        <span class="font-semibold">Contract Document</span>
      </template>
      <template #body>
        <div
          v-if="selectedDocumentType === 'pdf'"
          class="flex flex-col items-center justify-start w-full gap-8"
        >
          <iframe
            v-if="selectedDocumentUrl"
            :src="selectedDocumentUrl"
            type="application/pdf"
            width="90%"
            height="550px"
            style="border: 0"
          />
          <vs-button
            class="btn-download py-1 w-64 h-12"
            type="button"
            @click="downloadContractDocument"
          >
            <div class="flex flex-row items-center justify-start pr-2 w-full">
              <img
                :src="require('@/assets/shipo-icon-download.svg')"
                class="flex-shrink-0 mr-2 h-6"
              >
              <div class="truncate">
                Download File
              </div>
            </div>
          </vs-button>
        </div>
        <div v-else>
          <img
            v-if="selectedDocumentUrl"
            :src="selectedDocumentUrl"
            alt="Barge Photo"
            class="w-full h-auto object-contain rounded-3xl border-2"
          >
        </div>
      </template>
    </base-custom-modal>
  </div>
</template>

<script>

import {mapGetters} from "vuex";

import api from "@/api";
import BaseCustomModal from "@/components/baseCustomModal";
import DataTable from "@/components/dataTable";
import DatePicker from '@/components/datePicker';
import PopUp from "@/components/popUp/index.vue";
import SelectWithMultiple from "@/components/selectWithMultiple";
import SelectWithSearch from "@/components/selectWithSearch";
import TableBottom from "@/components/tableBottom";
import TableTop from "@/components/tableTop";
import { PAGE_TITLE } from "@/fixtures/Constants";
import downloadUtils from "@/utils/downloadUtils";

export default {
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  components: {
    BaseCustomModal,
    TableTop,
    TableBottom,
    DataTable,
    SelectWithSearch,
    PopUp,
    SelectWithMultiple,
    DatePicker
  },
  data() {
    return {
      isBusy: false,
      popUp: {
        isShown: false,
        tempId: null,
        isRestoreShown: false
      },
      popUpSuccess: false,
      spotCostHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '20px' },
        { key: 'owner_id', label: 'Owner', sortable: true },
        { key: 'barge_size', label: 'Size', sortable: true, width: '60px' },
        { key: 'route', label: 'Route', sortable: true },
        { key: 'contract_number', label: 'Contract No.', sortable: true },
        { key: 'rent_price_idr', label: 'Inc. VAT \n' + '(IDR/Ton)', sortable: true },
        { key: 'contract_end', label: 'Expiry Date', width: '185px', sortable: true },
        { key: 'remark', label: 'Description', sortable: false },
        { key: 'attachments', label: 'Contract Document', sortable: false },
        { key: 'status_approval', label: 'Status', sortable: true }
      ],
      spotCostData: [],
      restoreData: {
        id: null,
        contract_number: null,
        formData: {}
      },
      filters: {
        sort_by: '',
        sort_dir: '',
        owner: []
      },
      ownerOptions: [],
      routeOptions: [],
      isAdding: false,
      isUploading: false,
      isDocumentModalShown: false,
      selectedDocumentUrl: null,
      selectedDocumentType: null,
      isDownload: false,
      isProcessing: false
    }
  },
  computed: {
    ...mapGetters({
      spotCosts: 'SpotCost/spotCosts',
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      routes: 'SpotCost/routes',
      owner: 'SpotCost/owners',
      spotCostsFiles: 'SpotCost/spotCostsFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  mounted() {
    this.fetchOwners()
    this.fetchSpotCosts()
    this.fetchRoutes()
    if (this.$route.query.acknowledgeSuccess === 'true') {
      this.showSuccessNotification(this, 'Freight cost information has been successfully acknowledged.')
    }
    if (this.$route.query.acknowledgeFailed === 'true') {
      this.showFailedNotification(this, this.$route.query.acknowledgeMessage)
    }
  },
  watch: {
    spotCosts: {
      handler(val) {
        if (val) {
          this.spotCostData = val.map(item =>( {
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    routes: {
      handler(val) {
        if (val) {
          this.routeOptions = val.reduce((acc, item) => {
            if (!item.type_port) return acc

            const existingGroup = acc.find(group => group.title === item.type_port)

            const option = {
              value: item.alias,
              text: item.name
            };

            if (existingGroup) {
              existingGroup.options.push(option)
            } else {
              acc.push({
                title: item.type_port,
                options: [option]
              })
            }

            return acc;
          }, [])
        }
      }
    },
    owner: {
      handler(val) {
        if (val) {
          this.ownerOptions = val.map(item => ({
            value: item.id,
            text: item.name
          }))
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.spotCostHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.spotCostHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '75px' });
        } else if (!val && index !== -1) {
          this.spotCostHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchSpotCosts()
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadSpotCosts();
        }
      }
    },
    spotCostsFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, 'Spot_costs.xlsx')
          // Reset the companiesFiles state
          this.$store.dispatch('SpotCost/resetSpotCostsFiles');
        }
      }
    }
  },
  methods: {
    async fetchOwners() {
      try {
        await this.$store.dispatch('SpotCost/fetchOwners')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Owner, please try again')
      }
    },
    async fetchSpotCosts() {
      try {
        this.isAdding = false;
        this.isBusy = true;
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            if (key === 'owner') {
              item.owner = this.filters.owner.map(owner => owner.value) || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        await this.$store.dispatch(
          'SpotCost/fetchSpotCosts',
          {
            ...apiFilters
          }
        )
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch freight cost list, please try again')
      } finally {
        this.isBusy = false
      }
    },
    async fetchRoutes() {
      try {
        await this.$store.dispatch('SpotCost/fetchRoutes')
      } catch (e) {
        this.showFailedNotification(this, 'Failed to fetch Routes, please try again')
      }
    },
    async deleteSpotCost() {
      try {
        this.isProcessing = true;
        await this.$store.dispatch('SpotCost/deleteSpotCost', this.popUp.tempId)
        this.popUp = { isShown: false, tempId: null }
        this.showSuccessNotification(this, 'Freight Cost successfully deleted.')

        await this.fetchSpotCosts()
        this.isProcessing = false;
      } catch (e) {
        this.isProcessing = false;
        this.showFailedNotification(
          this,
          'Failed to delete Freight Cost , please try again',
          'Error'
        )
      }
    },
    async resetTableFilter() {
      await this.$store.dispatch('Table/resetConfig')
      await this.fetchSpotCosts()
      this.popUpSuccess = false
    },
    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir

      this.fetchSpotCosts()
    },
    resetFilters() {
      this.filters = {
        sort_by: '',
        sort_by_name: '',
        owner: []
      };
      this.$refs.tableTop.$emit('reset-search')
      this.fetchSpotCosts()
    },
    isNumber(value) {
      return typeof value === 'number' || (!isNaN(value) && value !== '' && value !== null);
    },
    startAdd() {
      // Cancel any existing edit
      if (this.isAdding || this.spotCostData.some(row => row.isEditing)) {
        this.showFailedNotification(this, 'Only one action can be active at a time. \n' +
          'Please save your changes first.')
        return
      }

      // Create new row
      const newRow = {
        id: `new-${Date.now()}`, // Temporary ID for new row
        isEditing: true,
        active: true,
        editForm: {
          owner_id: { value: '', isValid: true },
          barge_size: { value: '', isValid: true },
          route: { value: [], isValid: true },
          contract_number: { value: '', isValid: true },
          rent_price_idr: { value: null, isValid: true },
          contract_end: { value: '', isValid: true },
          remark: { value: '' },
          attachments: { value: null, isValid: true }
        }
      }

      this.isAdding = true
      this.spotCostData.unshift(newRow)
    },
    startEdit(row) {
      // Cancel any existing edit or add
      if (this.isAdding || this.spotCostData.some(r => r.isEditing)) {
        this.showFailedNotification(
          this,
          'Only one action can be active at a time. \n' + 'Please save your changes first.',
          'Error'
        )
        return
      }

      let routes = row.route
        .split(' - ')
        .map((routeText) => {
          const group = this.routeOptions.find(group =>
            group.options.some(option => option.value?.trim() === routeText?.trim())
          );

          const matchedOption = group?.options.find(option => option.value?.trim() === routeText?.trim());

          return matchedOption ?? null;
        })

      if (routes.some((item) => item === null)) routes= []

      const index = this.spotCostData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        this.$set(this.spotCostData[index], 'isEditing', true)
        this.$set(this.spotCostData[index], 'editForm', {
          barge_size: { value: row.barge_size, isValid: true },
          contract_number: { value: row.contract_number, isValid: true },
          rent_price_idr: { value: row.rent_price_idr, isValid: true },
          contract_end: { value: row.contract_end, isValid: true },
          remark: { value: row.remark },
          owner_id: {
            value: row.owner_name
              ? this.ownerOptions.find(option => option.text === row.owner_name)?.value
              : '',
            isValid: true
          },
          route: {
            value: routes,
            isValid: true
          },
          attachments: { value: row.attachments[0], isValid: true }
        })
      }
    },
    cancelAllEdits() {
      this.spotCostData.forEach(row => {
        if (row.isEditing) {
          this.$set(row, 'isEditing', false)
          this.$set(row, 'editForm', null)
        }
      })
      this.isAdding = false
    },
    cancelEdit(row) {
      if (this.isAdding) {
        // Remove the new row if we're canceling an add
        const index = this.spotCostData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.spotCostData.splice(index, 1)
        }
      } else {
        // Just cancel the edit
        const index = this.spotCostData.findIndex(item => item.id === row.id)
        if (index !== -1) {
          this.$set(this.spotCostData[index], 'isEditing', false)
          this.$set(this.spotCostData[index], 'editForm', null)
        }
      }
      this.isAdding = false
    },
    async saveEdit(row) {
      if (this.validateEditForm(row.editForm)) {
        this.showFailedNotification(
          this,
          'Some fields is empty, please fill it first',
          'Failed'
        )
        return
      }

      try {
        const filteredForm = JSON.parse(JSON.stringify(row)) // Deep clone row object
        if (!this.isAdding) {
          // Only maintain contract_number and changed field for edit
          filteredForm.editForm = this.filterUpdatedField(filteredForm)
        }
        const formData = {
          ...Object.keys(filteredForm.editForm).reduce((item, key) => {
            if (key === 'route') {
              item[key] = filteredForm.editForm[key].value.map((route) => route.value).join(' - ')
            } else if (key === 'attachments') {
              item[key] = filteredForm.editForm[key]
            } else if (key === 'barge_size') {
              item[key] = filteredForm.editForm[key].value.toString()
            } else {
              item[key] = filteredForm.editForm[key].value
            }
            return item
          }, {})
        }
        this.isProcessing = true
        const isRestoreData = await this.checkRestoreData(formData)
        if (isRestoreData) {
          this.isProcessing = false
          return
        }

        // Get attachment upload details; Use file field to differ either contract attachment updated or not
        if (row.editForm?.attachments?.file) {
          formData['attachment'] = await this.uploadAttachment(row.editForm.attachments.file)
          delete formData.attachments
        }

        if (this.isAdding) {
          // Handle add
          await this.$store.dispatch('SpotCost/postSpotCost', formData)
          this.showSuccessNotification(this, 'Freight Cost successfully added.')
        } else {
          // Handle edit
          formData['restore'] = false
          await this.$store.dispatch('SpotCost/putSpotCost', {
            id: row.id,
            form: formData
          })
          this.showSuccessNotification(this, 'Freight Cost successfully updated')
        }

        // Reset state
        this.cancelAllEdits()
        this.isProcessing = false
        await this.fetchSpotCosts()
      } catch ({ error }) {
        this.isProcessing = false
        this.showFailedNotification(
          this,
          error?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    validateEditForm(form) {
      Object.keys(form).forEach(key => {
        if (form[key].hasOwnProperty('isValid')) {
          form[key].isValid = !!form[key].value
        }
      })

      return Object.keys(form).some(key =>
        form[key].hasOwnProperty('isValid')
          ? !form[key].isValid
          : false
      )
    },
    async handleFileUpload(event, row) {
      const file = event.target.files[0]
      if (file) {
        const reader = new FileReader()
        reader.onload = (e) => {
          const base64 = e.target.result
          row.editForm.attachments = {
            value: base64,
            file: file
          }
        }
        reader.readAsDataURL(file)
      }
    },
    async openDocumentModal(row) {
      if (row.attachments && row.attachments.length) {
        const { data } = await api.getSpotCostAttachment(row.attachments[0].dms_node_id)
        this.selectedDocumentUrl = data
        this.selectedDocumentType = row.attachments[0].ext
        this.isDocumentModalShown = true
      }
    },
    closeRestoreModal() {
      this.popUp.isRestoreShown = false
      this.restoreData = {
        id: null,
        contract_number: null,
        formData: {}
      }
    },
    async restoreSpotCostData() {
      try {
        if (this.restoreData.formData.attachments ) {
          if (this.restoreData.formData.attachments.file) {
            this.restoreData.formData['attachment'] = await this.uploadAttachment(this.restoreData.formData.attachments.file)
          }
          delete this.restoreData.formData.attachments
        }

        await this.$store.dispatch('SpotCost/putSpotCost', {
          id: this.restoreData.id,
          form: {
            ...this.restoreData.formData,
            restore: true
          }
        })
        this.showSuccessNotification(this, 'Freight Cost successfully updated')

        // Reset state
        this.cancelAllEdits()
        this.closeRestoreModal()
        await this.fetchSpotCosts()
      } catch (e) {
        this.showFailedNotification(
          this,
          e?.message || 'Failed to save changes, please try again',
          'Error'
        )
      }
    },
    async checkRestoreData(formData) {
      try {
        const { data: {
          id,
          contract_number,
          is_deleted
        }} = await api.getSpotCostDetails({
          contract_number: formData.contract_number
        })
        if (is_deleted) {
          this.popUp.isRestoreShown = true
          this.restoreData.id = id
          this.restoreData.contract_number = contract_number
          this.restoreData.formData = formData

          return true
        }

        return false
      } catch (e) {
        this.showFailedNotification(
          this,
          e?.message || 'Failed to restore data, please try again',
          'Error'
        )
      }
    },
    downloadContractDocument() {
      const a = document.createElement('a')
      const url = new URL(this.selectedDocumentUrl);
      url.searchParams.set('attachment', 'true');
      a.href = url.toString()
      a.download = ''
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
    },
    handleAddRoute(searchTerm) {
      const newRouteOption = { value: searchTerm, text: searchTerm }
      this.routeOptions[this.routeOptions.length - 1].options.push(newRouteOption)

      const editedRow = this.spotCostData.find((data) => data.isEditing)
      editedRow.editForm.route.value.push(newRouteOption)
    },
    async uploadAttachment(file) {
      const formData = new FormData()
      formData.append('attachment', file)
      const { data } = await api.uploadAttachment(formData)

      return data
    },
    filterUpdatedField(row) {
      Object.keys(row.editForm).map((key) => {
        if (key === 'attachments' && !row.editForm[key].file) {
          delete row.editForm[key]
        } else if (key !== 'contract_number') {
          if (key === 'route') {
            if (row.editForm[key].value.map((route) => route.value).join(' - ') === row[key]) delete row.editForm[key]
          }
          else if (row.editForm[key].value === row[key]) delete row.editForm[key]
        }
      })

      return row.editForm
    },
    async downloadSpotCosts() {
      try {
        this.isBusy = true
        const apiFilters = Object.keys(this.filters)
          .filter(key => !!this.filters[key])
          .reduce((item, key) => {
            if (key === 'owner') {
              item.owner = this.filters.owner.map(owner => owner.value) || null
            } else {
              item[key] = this.filters[key]
            }
            return item
          }, {})
        await this.$store.dispatch(
          'SpotCost/downloadSpotCosts',
          apiFilters,
          {
            headers: {
              accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            },
            responseType: 'blob'
          }
        )
        this.isDownload = false;
      } catch (error) {
        this.isDownload = false
        this.showFailedNotification(this, 'Failed to download freight costs, please try again');
      } finally {
        this.isBusy = false
      }
    }
  },
  beforeCreate() {
    this.$store.dispatch('Table/resetConfig')
  }
}
</script>

<style lang="scss" scoped>
/deep/.vs-textarea {
  min-height: 20px;
  height: calc(1rem * 10 / 3);
  resize: none;
}

.filter-container {
  @apply p-5 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply w-8;
  @apply h-8;
  background-color: #493493;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.bg-contract {
  @apply py-1;
  @apply px-3;
  @apply font-semibold;
  @apply rounded-lg;

  &.new {
    @apply text-primary;
    background-color: #F1F1FD;
  }

  &.updated {
    @apply text-danger;
    @apply bg-danger-accent;
  }
}

.is-not-valid {
  /deep/.vs-con-input,
  /deep/.mx-input-wrapper,
  /deep/.bg-accent-multiselect {
    @apply rounded-lg;
    outline: 2px solid #ff647c;
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}

.btn-download {
  background-color: rgba(76, 175, 80, 0.2);
  color: #4CAF50;
}

.badge-status {
  background-color: #4CAF5033;
  color: #4CAF50;
  text-align: center;
  padding: 5px 0;
  border-radius: 5px;

  &.inactive {
    background-color: rgba(100, 100, 100, 0.2);
    color: #646464
  }
}
</style>